import ApiService from './api';

/** Tone-of-Voice configuration of a single channel. */
interface ToneOfVoiceChannelSettings {
  /** Channel the settings belong to */
  channelId: string;
  /** Whether the tone of voice feature is enabled or not */
  enabled: boolean;
  /** Example pairs the tone is derived from */
  examples: {
    /** The situation the example answer applies to */
    scenario: string;
    /** The example answer written in the desired tone */
    answer: string;
  }[];
}

/** Persona configuration of a single channel. */
interface PersonaChannelSettings {
  /** Channel the settings belong to */
  channelId: string;
  /** Persona presented by the bot on this channel */
  persona: {
    /** Title/name shown for the agent */
    agentTitle: string;
    /** Short description of the agent */
    agentDescription: string;
  };
}

const SettingsService = {
  /**
   * @param botId Bot the widget belongs to
   * @param channelId Channel whose widget-editor config is fetched
   * @returns Config, or `{error}` on failure
   */
  getChannelConfig: async function (botId: string, channelId: string): Promise<any> {
    try {
      const response = await ApiService.get(`/schaltzentrale/widget/editor/${botId}/${channelId}`);
      return response.data;
    } catch (error) {
      return { error };
    }
  },
  /**
   * @param config Full widget-editor configuration to persist
   */
  saveChannelConfig: async function (
    botId: string,
    channelId: string,
    config: any
  ): Promise<any> {
    try {
      const response = await ApiService.post(`/schaltzentrale/widget/editor/${botId}/${channelId}`, config);
      return response.data;
    } catch (error) {
      return { error };
    }
  },
  /**
   * @param state Whether the channel widget should be active
   */
  saveChannelState: async function (
    botId: string,
    channelId: string,
    state: boolean
  ): Promise<any> {
    try {
      const response = await ApiService.post(`/schaltzentrale/widget/editor/active/${botId}/${channelId}`, { active: state });
      return response.data;
    } catch (error) {
      return { error };
    }
  },
  /**
   * @param provider OAuth provider key, e.g. 'facebook'
   * @param token OAuth token placed in the URL path; omitted from the path when falsy
   * @param url Optional callback/shop URL, URL-encoded into the path when set
   * @param tokenObj Optional token payload sent as the request body
   */
  saveOAuthToken: async function (
    botId: string,
    provider: string,
    token?: string | null,
    url?: string | null,
    tokenObj?: any
  ): Promise<any> {
    const urlParam = url ? `/${encodeURIComponent(url)}` : '';
    try {
      const response = await ApiService.post(`/bot/oauth/${botId}/${provider}/${token ? token : ''}${urlParam}`, tokenObj);
      return response.data;
    } catch (error) {
      return { error };
    }
  },
  /**
   * @param settingType Setting key name under `bot.settings`
   */
  getSettings: async function (botId: string, settingType: string): Promise<any> {
    try {
      const response = await ApiService.get(`/schaltzentrale/bot/settings/${botId}/${settingType}`);
      return response.data;
    } catch (error) {
      return { error };
    }
  }
  ,
  /**
   * @param setting New value for the setting (request body)
   * @param settingType Setting key name under `bot.settings`
   */
  setSettings: async function (
    botId: string,
    setting: any,
    settingType: string
  ): Promise<any> {
    try {
      const response = await ApiService.post(`/schaltzentrale/bot/settings/${botId}/${settingType}`, setting);
      return response.data;
    } catch (error) {
      return { error };
    }
  },
  getFBChannels: async function (botId: string): Promise<any> {
    try {
      const response = await ApiService.get(`/bot/oauth/${botId}/facebook/getChannels`);
      return response.data;
    } catch (error) {
      return { error };
    }
  },
  // Get general information for provider and their version. Responce example: { livechat: ['default', 'version2'] }
  getSettingsOverview: async function (botId: string): Promise<any> {
    try {
      const response = await ApiService.get(`/schaltzentrale/bot/${botId}/settings`);
      return response.data;
    } catch (error) {
      return { error };
    }
  },
  /**
   * Retrieves the Tone of Voice settings from all channels of the provided bot
   * @param botId The current bot ID
   * @returns Null if error, else the settings per channel
   */
  getToneOfVoiceSettings: async (
    botId: string
  ): Promise<ToneOfVoiceChannelSettings[] | null> => {
    try {
      const response = await ApiService.get(`/schaltzentrale/bot/${botId}/channels/tone-of-voice`);
      return response?.data ?? null;
    } catch (error) {
      console.error('[getToneOfVoiceSettings] Error in request. Error: ', error);
      return null;
    }
  },
  /**
   * Stores the Tone of Voice settings for a given channel in the backend
   * @param botId The ID of the bot
   * @param channelId The ID of the channel, or `null` for the default/first channel
   * @param enabled Whether the tone of voice feature is enabled or not
   * @param rules The tone rules/answers per scenario, ordered by index
   * @returns The new state of the entire channel, or `null` on error
   */
  setToneOfVoiceSettings: async (
    botId: string,
    channelId: string | null,
    enabled: boolean,
    rules: any
  ): Promise<any> => {
    try {
      const response = await ApiService.put(`/schaltzentrale/bot/${botId}/channels/${channelId}/tone-of-voice`, {
        enabled,
        rules,
      });
      return response?.data ?? null;
    } catch (error) {
      console.error('[setToneOfVoiceSettings] Error in request. Error: ', error);
      return null;
    }
  },
  /**
   * Retrieves the persona settings from all channels of the provided bot
   * @param botId The current bot ID
   * @returns Null if error, else the settings per channel
   */
  getPersonaSettings: async (
    botId: string
  ): Promise<PersonaChannelSettings[] | null> => {
    try {
      const response = await ApiService.get(`/schaltzentrale/bot/${botId}/channels/persona`);
      return response?.data ?? null;
    } catch (error) {
      console.error('[getPersonaSettings] Error in request. Error: ', error);
      return null;
    }
  },
  /**
   * Stores the Persona settings for a given channel in the backend
   * @param botId The ID of the bot
   * @param channelId The ID of the channel, or `null` for the default/first channel
   * @param persona The persona fields (agentTitle/agentDescription) to store
   * @returns The new state of the entire channel, or `null` on error
   */
  setPersonaSettings: async (
    botId: string,
    channelId: string | null,
    persona: any
  ): Promise<any> => {
    try {
      const response = await ApiService.put(`/schaltzentrale/bot/${botId}/channels/${channelId}/persona`, persona);
      return response?.data ?? null;
    } catch (error) {
      console.error('[setToneOfVoiceSettings] Error in request. Error: ', error);
      return null;
    }
  },
  /**
   * Updates channel settings (not all fields are allowed by validator)
   *
   */
  updateChannel: async ({
    botId,
    channelId,
    params,
  }: {
    /** Bot the channel belongs to */
    botId: string;
    /** Channel to update */
    channelId: string;
    /** Channel fields to update; backend validator restricts which are allowed */
    params: any;
  }): Promise<
    | { success: true; data: any }
    | { success: false; error: { message: any } }
  > => {
    const LOG_PREFIX = `[updateChannel]`;
    try {
      const response = await ApiService.put(`/schaltzentrale/bot/${botId}/channels/${channelId}`, params);
      return { success: true, data: response?.data ?? null };
    } catch (error: any) {
      console.error(`${LOG_PREFIX} Error when updating channel. Error: `, error);

      let validationError = '';
      if (error.data?.result?.[0]?.message) validationError = `Validation error! ${error.data?.result?.[0]?.message}`;
      const message = validationError || error.message || error;

      return { success: false, error: { message } };
    }
  },
  /**
   * Retrieves all the settings for a given live chat provider
   * @returns Will return null if bot does not have this provider
   */
  getProviderSettings: async function ({
    botId,
    provider,
  }: {
    /** The current bot ID */
    botId: string;
    /** The live chat provider, e.g. 'salesforce' */
    provider: string;
  }): Promise<any> {
    try {
      const response = await ApiService.get(`/schaltzentrale/bot/${botId}/settings/${provider}`);
      return response?.data ?? null;
    } catch (error: any) {
      // { message: 'Bot doesn't have 'salesforce' provider', code: 10002, status: 400 }
      const providerMissing =
        error?.code === 10002 &&
        error?.status === 400 &&
        typeof error?.message === 'string' &&
        error.message.includes(`doesn't have '${provider}' provider`);

      if (!providerMissing) {
        console.error('[getProviderSettings] Error in request. Error: ', error);
      }
      return null;
    }
  },
  /**
   * Overwrites all the settings for a given live chat provider
   * @returns `{success, data}` on success, `{success: false, error}` on failure
   */
  setProviderSettings: async function ({
    botId,
    provider,
    config,
  }: {
    /** The current bot ID */
    botId: string;
    /** The live chat provider */
    provider: string;
    /** The new settings */
    config: any;
  }): Promise<{ success: boolean; data?: any; error?: any }> {
    try {
      const response = await ApiService.post(`/schaltzentrale/bot/${botId}/settings/${provider}`, config);
      return { success: true, data: response.data };
    } catch (error: any) {
      const errorData = error?.response?.data?.error || error?.response?.data || error;
      return { success: false, error: errorData };
    }
  },
  /**
   * Get provider settings
   * @returns Will return null if bot does not have this provider or version
   */
  getProviderVersionSettings: async function ({
    botId,
    provider,
    key,
  }: {
    /** The current bot ID */
    botId: string;
    /** The live chat provider */
    provider: string;
    /** Version key of the provider settings, e.g. 'default' or 'version2' */
    key: string;
  }): Promise<any> {
    try {
      const response = await ApiService.get(`/schaltzentrale/bot/${botId}/settings/${provider}/${key}`);
      return response?.data ?? null;
    } catch (error) {
      return { error };
    }
  },
  addProviderVersionSettings: async function ({
    botId,
    provider,
    key,
    config,
  }: {
    /** The current bot ID */
    botId: string;
    /** The live chat provider */
    provider: string;
    /** Version key of the provider settings */
    key: string;
    /** Settings to create under this version key */
    config: any;
  }): Promise<any> {
    try {
      const response = await ApiService.post(`schaltzentrale/bot/${botId}/settings/${provider}/${key}`, config);
      return response.data;
    } catch (error) {
      return { error };
    }
  },
  updateProviderVersionSettings: async function ({
    botId,
    provider,
    key,
    config,
  }: {
    /** The current bot ID */
    botId: string;
    /** The live chat provider */
    provider: string;
    /** Version key of the provider settings */
    key: string;
    /** New settings to store under this version key */
    config: any;
  }): Promise<{ success: boolean; data?: any; error?: any }> {
    try {
      const response = await ApiService.put(`schaltzentrale/bot/${botId}/settings/${provider}/${key}`, config);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.log(error)
      const errorData = error?.response?.data?.error || error?.response?.data || error;
      return { success: false, error: errorData };
    }
  },
  deleteProviderVersionSettings: async function ({
    botId,
    provider,
    key,
  }: {
    /** The current bot ID */
    botId: string;
    /** The live chat provider */
    provider: string;
    /** Version key of the provider settings */
    key: string;
  }): Promise<any> {
    try {
      const response = await ApiService.delete(`schaltzentrale/bot/${botId}/settings/${provider}/${key}`);
      return response.data;
    } catch (error) {
      return { error };
    }
  },
  /**
   * Perform a test with given settings
   * @param botId The current bot ID
   * @param settingToTest The identifier for what should be tested
   * @param values The values that to test with
   * @returns `{error}` on failure, otherwise the backend test result (e.g. `{success: true}`)
   */
  async testBotSetting(
    botId: string,
    settingToTest: string,
    values: any
  ): Promise<any> {
    try {
      const response = await ApiService.post(`schaltzentrale/bot/${botId}/settings/${settingToTest}/test`, values);
      return response.data;
    } catch (error) {
      return { error };
    }
  },
  /**
   * Patches in the fields you defined into a setting in the bot
   * @param botId The current bot ID
   * @param setting The setting key name (`bot.settings[setting]`)
   * @param partialNewSettings Partial new value to merge into the existing settings
   * @returns If not error, returns the full new settings object
   */
  async patchSetting(
    botId: string,
    setting: string,
    partialNewSettings: object | any[]
  ): Promise<any> {
    try {
      const response = await ApiService.patch(`schaltzentrale/bot/${botId}/settings/${setting}`, partialNewSettings);
      return response.data;
    } catch (error) {
      return { error };
    }
  }
};

export default SettingsService;

export { SettingsService };
